import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { calculateReadingTime, formatTimeAgo, getFirstParagraphText, getFirstTitleFromHtml } from '../../../helpers/utilities';
import ProfilePicture from '../ProfilePicture/ProfilePicture';
import './NewsItem.scss';
import Icon from '../Icon/Icon';
import {
    fetchSaveUnSaveNews,
    saveUnSaveNews,
    fetchLikeNews,
    fetchUnlikeNews,
    likeUnlikeNews,
    isNewsLikedByUser,
} from '../../Reducers/actions/newsActions';
import { useNavigate } from 'react-router-dom';
import ContributionSection from '../ContributionSection/ContributionSection';
import { useState } from 'react';
import { saveScrollPosition } from '../../Reducers/actions/navigationActions';
import NewsItemSkeleton from './NewsItemSkeleton';
import { useSoftwall } from '../../containers/SoftWall/SoftWall';
import { getProfilePublicPath } from '../../../helpers/profilePaths';

const NewsItem = ({ news, handleShowComments, type = 'user', non_interactive = false }) => {
    const sw = useSoftwall();
    const dispatch = useDispatch();
    const user_id = useSelector(state => state.auth.user?.user_id);
    const navigate = useNavigate()
    const [showContribution, setShowContribution] = useState(false);

    const handleLikeUnlike = (e) => {
        if (!sw.requireAuth("Like/Unlike News","Sign up or log in to like this news")) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (!user_id) return;

        const unlike = isNewsLikedByUser(news, user_id);
        const apiCall = unlike ? fetchUnlikeNews : fetchLikeNews;
        apiCall(news.id).then(() => {
        }).catch(err => {
            console.error('Error liking/unliking news:', err);
        });
        dispatch(likeUnlikeNews(news.id, !unlike, user_id));
    }
    const handleNativeShare = async (e, news) => {
        e.preventDefault();
        e.stopPropagation();
        if (navigator.share) {
            try {
                // Extract meaningful title from HTML content
                const newsTitle = getFirstTitleFromHtml(news.content) || news.title || 'News Article';
                const shareTitle = `Check out "${newsTitle}"`;

                await navigator.share({
                    title: shareTitle,
                    url: `${window.location.origin}/news/${news.id}`
                });
            } catch (err) {
                console.error("Error sharing:", err);
            }
        } else {
            alert("Sharing is not supported on this device.");
        }
    }

    const handleSaveUnsave = (e) => {
        if (!sw.requireAuth("save/Unsave News","Sign up or log in to save this news")) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (!user_id) return;
        const unsave = news?.is_saved?.some(saved => saved.user_id === user_id);
        fetchSaveUnSaveNews(news.id, unsave)
            .then(() => {
            })
            .catch(err => {
                console.error('Error saving/unsaving news:', err);
            });

        dispatch(saveUnSaveNews(news.id, !unsave, user_id));
    }

    const handleClickNews = () => {
        // Save current scroll position before navigating to news detail
        const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        if (currentScrollPosition > 0) {
            dispatch(saveScrollPosition('news', 'main', currentScrollPosition));
        }
        navigate(`/news/${news.id}${news.users?.user_id === user_id ? '?from_profile=true' : ''}`)
    }

    const handleContribute = async () => {
        setShowContribution(true)
    }
    const is_saved = news?.is_saved?.some(saved => saved.user_id === user_id);
    const is_liked = isNewsLikedByUser(news, user_id);
    return (
        <div className={`new-card-container mb-24 ${type !== 'edit' ? 'border-bottom' : ''}`}>
            {showContribution && <ContributionSection
                creation={{
                    creation_id: null,
                    title: `Creator ${news?.users?.userName || `${news?.users?.firstName || ''} ${news?.users?.lastName || ''}`.trim() || 'Unknown User'}`,
                    description: "Contribution to a creator",
                    creator_id: news?.users?.user_id,
                    thumbnail_url: news?.users?.profilePicture,
                    users: news?.users
                }}
                onClose={() => {
                    setShowContribution(false)
                }}
            />}
            <div className='news-header d-flex aic mb--4 mx-16'>
                <ProfilePicture imgUrl={news.users?.profilePicture} no_edit={true} showIcon={false} width={32} height={32} handleClick={() => {
                    const p = getProfilePublicPath(news.users);
                    if (p) navigate(p);
                }} />
                <p className='fs-regular fg-dark fw-medium-bold my-0 ml-8 tt-uppercase'>
                    {news.users?.userName || `${news.users?.firstName || ''} ${news.users?.lastName || ''}`.trim() || 'Unknown User'}
                </p>
            </div>
            <div className='news-card' key={news.id} onClick={handleClickNews}>

                <div className='news-content d-flex'>
                    <div>
                        <p className='fg-dark fw-medium-bold fs-regular mt-0 mb-0 title-news'>{news.title}</p>
                        <p className='fs-xs fw-medium fg-grey7 my-0 ml-auto d-flex aic'>{calculateReadingTime(news.content)} min read <span className='dot'></span> {formatTimeAgo(news.created_at)}</p>

                        <p className='fs-xs fg-dark my-0 fw-medium content-itself'>{getFirstParagraphText(news.content)}</p>
                    </div>
                    {news.media_url?.length ? (
                        <div className='img-news-list ml-auto'><img src={news.media_url} alt={news.title} width={108} height={108} /></div>
                    ) : (
                        <div className="ml-auto img-news-list img-placeholder d-flex f-column aic jcc">
                        </div>
                    )}
                </div>
            </div>
            <div className='news-footer d-flex aic mt-12 mx-16'>
                <span className="d-flex" onClick={non_interactive ? undefined : handleLikeUnlike}>{!is_liked ? <Icon alias={"like"} color="#282828" stroke={"#282828"} width={24} /> : <Icon alias={"liked_v2"} width={24} height={24} color='#FF8CDC' />}</span>
                <p className="fs-xxs fg-crea fw-medium-bold ml-8 my-0">{news?.total_likes || ''}</p>
                <span className="d-flex ml-16" onClick={(e) => non_interactive ? undefined : handleShowComments(e, news)}><Icon alias={"comment"} stroke="#282828" color='none' width={24} /></span>
                <p className="fs-xxs fg-crea fw-medium-bold ml-8 my-0">{news?.total_comments || ''}</p>
                <span className="d-flex ml-16" onClick={non_interactive ? undefined : handleSaveUnsave}>
                    {!is_saved ?
                        <Icon alias={"star"} strokeWidth={2} color="#282828" width={24} />
                        : <Icon alias={"saved_star"} color="#FF8CDC" stroke='#FF8CDC' strokeWidth={2} width={24} />}
                </span>
                <span className="d-flex" onClick={(e) => non_interactive ? undefined : handleNativeShare(e, news)}><Icon alias={"share"} stroke="#282828" color='none' width={24} className={"ml-24"} /></span>
                <button onClick={handleContribute} className="contribute-btn btn-sm btn-xs red-outline-btn d-flex aic ml-auto fs-tiny fw-medium">
                    <Icon alias={"credit_card"} color="#FF3232" stroke={'#FF3232'} width={16} className={'mr-4'} />
                    <span className='fs-tiny fw-extra-bold'>Contribute</span>
                </button>
            </div>
        </div>
    )
}

NewsItem.propTypes = {
    news: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        title: PropTypes.string.isRequired,
        content: PropTypes.string,
        created_at: PropTypes.string,
        media_url: PropTypes.string,
        likes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        comments: PropTypes.arrayOf(PropTypes.object),
        saved: PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
        ]),
        users: PropTypes.shape({
            user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            profilePicture: PropTypes.string
        })
    }).isRequired,
    handleShowComments: PropTypes.func.isRequired,
    type: PropTypes.oneOf(['user', 'saved', 'all']),
    non_interactive: PropTypes.bool
};

export default NewsItem;